import {
  Activity,
  Briefcase,
  CheckCircle2,
  DollarSign,
  Eye,
  Send,
  TrendingUp,
  Users,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useCurrentUser, useDashboard } from "@/api/hooks";
import type { DashboardRangeKey } from "@/api/types";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import { WelcomeWizard } from "@/components/WelcomeWizard";
import { formatCents } from "@/lib/format";

const RANGE_OPTIONS: { value: DashboardRangeKey; label: string }[] = [
  { value: "this_month", label: "This month" },
  { value: "last_30_days", label: "Last 30 days" },
  { value: "last_90_days", label: "Last 90 days" },
  { value: "this_quarter", label: "This quarter" },
  { value: "this_year", label: "This year" },
  { value: "all_time", label: "All time" },
];

/** Admin home — at-a-glance metrics that answer "what's the business
 * doing right now?" Pencilout-admin sees platform totals; org users
 * see only their own org's slice. Same component, scope-aware
 * response shape from /dashboard.
 *
 * Range selector lets the contractor compare windows (this month vs.
 * last 90 days) without leaving the page. The funnel + per-user
 * sections recompute server-side on each switch — staleTime caches
 * the response per-range so toggling back is free. */
export function DashboardPage() {
  const me = useCurrentUser();
  const [range, setRange] = useState<DashboardRangeKey>("this_month");
  const dash = useDashboard(range);
  const isAdmin = me.data?.role === "pencilout_admin";

  if (dash.isLoading && !dash.data) {
    return <p className="text-sm text-graphite-soft">Loading metrics…</p>;
  }
  if (dash.error || !dash.data) {
    return (
      <p className="text-sm text-red-pencil">
        {dash.error instanceof ApiError ? dash.error.detail : "Could not load dashboard."}
      </p>
    );
  }

  const m = dash.data;

  return (
    <div className="space-y-6">
      {/* First-load welcome wizard. Self-dismisses via localStorage
          after the user clicks Skip / Get started / a CTA. Pencilout
          staff users see it once too — harmless, takes one click to
          dismiss for the lifetime of that browser. */}
      {!isAdmin && <WelcomeWizard />}

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-mono text-2xl font-bold tracking-tight">
            {isAdmin ? "Platform overview" : "Your dashboard"}
          </h1>
          <p className="mt-1 text-sm text-graphite-soft">
            {isAdmin
              ? "Across every organization on PencilOut."
              : `Activity for ${me.data?.organization_name ?? "your organization"}.`}
          </p>
        </div>
        <RangePicker value={range} onChange={setRange} label={m.range_label} />
      </div>

      {/* Onboarding checklist — auto-hides at 100% complete + when
          dismissed. Pencilout staff don't render it (server returns
          a passthrough). */}
      {!isAdmin && <OnboardingChecklist />}

      {/* ---- Pipeline tiles (mix of always-current + range-bound) ---- */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          icon={Briefcase}
          label="Active jobs"
          value={m.active_jobs.toLocaleString()}
          link="/"
        />
        <Tile
          icon={Activity}
          label="Walkthroughs (7d)"
          value={m.walkthroughs_last_7_days.toLocaleString()}
        />
        <Tile
          icon={Send}
          label="Quotes in flight"
          value={m.quotes_in_flight.toLocaleString()}
          sub={
            m.quotes_sent_in_range > 0
              ? `${m.quotes_sent_in_range} sent · ${m.range_label.toLowerCase()}`
              : `none sent · ${m.range_label.toLowerCase()}`
          }
        />
        <Tile
          icon={TrendingUp}
          label="Win rate (90d)"
          value={m.win_rate_pct === null ? "—" : `${m.win_rate_pct.toFixed(1)}%`}
          sub={
            m.win_rate_pct === null
              ? "no decided quotes yet"
              : `${m.quotes_accepted_in_range} won · ${m.quotes_declined_in_range} lost · ${m.range_label.toLowerCase()}`
          }
        />
      </div>

      {/* ---- Money tiles ---- */}
      <div
        className={[
          "grid grid-cols-1 gap-4",
          // Two-tile row for staff (Quote value + AI cost). Contractors
          // only see Quote value — AI cost is internal-vendor detail
          // that doesn't belong on a customer dashboard. Their
          // billing-relevant view (quotes used / included this
          // cycle) lives on /billing + the Jobs-list usage banner.
          isAdmin ? "sm:grid-cols-2" : "",
        ].join(" ")}
      >
        <BigTile
          icon={DollarSign}
          label={`Quote value won · ${m.range_label.toLowerCase()}`}
          value={formatCents(m.accepted_quote_value_cents_in_range)}
          accent
        />
        {isAdmin && (
          <BigTile
            icon={Activity}
            label={`AI cost (billed) · ${m.range_label.toLowerCase()}`}
            value={formatCents(m.ai_billed_cents_in_range)}
          />
        )}
      </div>

      {/* ---- Funnel ---- */}
      <FunnelStrip funnel={m.funnel} rangeLabel={m.range_label} />

      {/* ---- Per-user breakdown (only when scope has multiple users) ---- */}
      {m.per_user.length > 0 && <PerUserTable rows={m.per_user} rangeLabel={m.range_label} />}

      {/* ---- Admin-only platform totals ---- */}
      {isAdmin && (
        <div className="rounded-lg border border-rule bg-paper-warm p-5">
          <h2 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
            Platform
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
            <Mini icon={Users} label="Users" value={(m.users_count ?? 0).toLocaleString()} />
            <Mini
              icon={Briefcase}
              label="Organizations"
              value={(m.organizations_count ?? 0).toLocaleString()}
            />
            <Mini
              icon={CheckCircle2}
              label="Paying orgs"
              value={(m.paying_organizations_count ?? 0).toLocaleString()}
              sub={
                m.organizations_count
                  ? `${(((m.paying_organizations_count ?? 0) / m.organizations_count) * 100).toFixed(0)}% of total`
                  : undefined
              }
            />
          </div>
        </div>
      )}

      <div className="text-center font-mono text-xs text-graphite-soft">
        <Link to="/" className="underline">
          Browse jobs →
        </Link>
        {isAdmin && (
          <>
            {"  ·  "}
            <Link to="/usage" className="underline">
              AI usage report →
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------

function RangePicker({
  value,
  onChange,
  label,
}: {
  value: DashboardRangeKey;
  onChange: (v: DashboardRangeKey) => void;
  label: string;
}) {
  return (
    <label className="inline-flex items-center gap-2 rounded-lg border border-rule bg-paper-warm px-3 py-2">
      <span className="font-mono text-[11px] uppercase tracking-wider text-graphite-soft">
        Range
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as DashboardRangeKey)}
        className="bg-transparent font-mono text-sm font-semibold text-graphite focus:outline-none"
        aria-label={`Time range — ${label}`}
      >
        {RANGE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function FunnelStrip({
  funnel,
  rangeLabel,
}: {
  funnel: {
    sent: number;
    viewed: number;
    accepted: number;
    declined: number;
    expired: number;
  };
  rangeLabel: string;
}) {
  if (funnel.sent === 0) {
    return null;
  }
  // Conversion rates against the "sent" cohort. Capped at 100 so a
  // weird race (re-import / migration) where status counts > sent
  // shows reasonably rather than a 142% bar.
  const pct = (n: number) => Math.min(100, Math.round((n / funnel.sent) * 100));

  const stages: { label: string; count: number; icon: typeof Send }[] = [
    { label: "Sent", count: funnel.sent, icon: Send },
    { label: "Viewed", count: funnel.viewed, icon: Eye },
    { label: "Accepted", count: funnel.accepted, icon: CheckCircle2 },
    { label: "Declined", count: funnel.declined, icon: XCircle },
  ];

  return (
    <section className="rounded-lg border border-rule bg-paper-warm p-5">
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
          Funnel
        </h2>
        <span className="font-mono text-[11px] text-graphite-soft">
          Cohort: quotes sent · {rangeLabel.toLowerCase()}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {stages.map((s) => (
          <div key={s.label}>
            <div className="flex items-center gap-1.5 text-graphite-soft">
              <s.icon className="h-3 w-3" aria-hidden />
              <span className="text-[11px] font-medium uppercase tracking-wider">{s.label}</span>
            </div>
            <p className="mt-1 font-mono text-xl font-bold tabular-nums text-graphite">
              {s.count.toLocaleString()}
            </p>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-rule/40">
              <div className="h-full bg-graphite" style={{ width: `${pct(s.count)}%` }} />
            </div>
            <p className="mt-1 font-mono text-[10px] text-graphite-soft">{pct(s.count)}% of sent</p>
          </div>
        ))}
      </div>
      {funnel.expired > 0 && (
        <p className="mt-3 font-mono text-[11px] text-graphite-soft">
          {funnel.expired.toLocaleString()} expired without a decision.
        </p>
      )}
    </section>
  );
}

function PerUserTable({
  rows,
  rangeLabel,
}: {
  rows: {
    user_id: string;
    display_name: string;
    quotes_sent: number;
    quotes_accepted: number;
    accepted_value_cents: number;
  }[];
  rangeLabel: string;
}) {
  return (
    <section className="rounded-lg border border-rule bg-paper-warm p-5">
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
          By estimator
        </h2>
        <span className="font-mono text-[11px] text-graphite-soft">
          Top {rows.length} · {rangeLabel.toLowerCase()}
        </span>
      </div>
      <table className="w-full table-fixed text-sm">
        <thead>
          <tr className="text-left font-mono text-[11px] uppercase tracking-wider text-graphite-soft">
            <th className="w-[40%] pb-2">Estimator</th>
            <th className="pb-2 text-right">Sent</th>
            <th className="pb-2 text-right">Won</th>
            <th className="pb-2 text-right">Won value</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const winPct =
              row.quotes_sent > 0
                ? Math.round((row.quotes_accepted / row.quotes_sent) * 100)
                : null;
            return (
              <tr key={row.user_id} className="border-t border-rule">
                <td className="py-2 font-mono text-sm text-graphite">{row.display_name}</td>
                <td className="py-2 text-right font-mono tabular-nums text-graphite">
                  {row.quotes_sent.toLocaleString()}
                </td>
                <td className="py-2 text-right font-mono tabular-nums text-graphite">
                  {row.quotes_accepted.toLocaleString()}
                  {winPct !== null && (
                    <span className="ml-1 text-[11px] text-graphite-soft">({winPct}%)</span>
                  )}
                </td>
                <td className="py-2 text-right font-mono tabular-nums text-graphite">
                  {formatCents(row.accepted_value_cents)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function Tile({
  icon: Icon,
  label,
  value,
  sub,
  link,
}: {
  icon: typeof Briefcase;
  label: string;
  value: string;
  sub?: string;
  link?: string;
}) {
  const inner = (
    <div className="rounded-lg border border-rule bg-paper-warm p-4 transition hover:bg-pencil-yellow-soft">
      <div className="flex items-center gap-2 text-graphite-soft">
        <Icon className="h-3.5 w-3.5" aria-hidden />
        <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
      </div>
      <p className="mt-2 font-mono text-2xl font-bold tabular-nums text-graphite">{value}</p>
      {sub && <p className="mt-1 font-mono text-[11px] text-graphite-soft">{sub}</p>}
    </div>
  );
  return link ? <Link to={link}>{inner}</Link> : inner;
}

function BigTile({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: typeof DollarSign;
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border border-rule p-5 ${
        accent ? "bg-pencil-yellow-soft" : "bg-paper-warm"
      }`}
    >
      <div className="flex items-center gap-2 text-graphite-soft">
        <Icon className="h-4 w-4" aria-hidden />
        <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
      </div>
      <p className="mt-2 font-mono text-3xl font-bold tabular-nums text-graphite">{value}</p>
    </div>
  );
}

function Mini({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: typeof Users;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-graphite-soft">
        <Icon className="h-3 w-3" aria-hidden />
        <span className="text-[11px] font-medium uppercase tracking-wider">{label}</span>
      </div>
      <p className="mt-1 font-mono text-xl font-bold tabular-nums text-graphite">{value}</p>
      {sub && <p className="font-mono text-[10px] text-graphite-soft">{sub}</p>}
    </div>
  );
}
